"use client";

import React from "react";
import Head from "next/head";
import Link from "next/link";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle, Users, TrendingUp, MessageSquare, DollarSign, Mail, HelpCircle } from "lucide-react";

// Placeholder for AnimatedComponents if they exist, otherwise use framer-motion directly
// Example: import { AnimatedSection, AnimatedCard } from "@/components/animations";

const AdsPage: React.FC = () => {
  // Dummy data - replace with actual data or fetch from API
  const trafficStats = {
    monthlyVisitors: "5,000,000+",
    pageViews: "200,000,000+",
    avgSessionDuration: "4.5 min",
    reach: "Our platform connects you with engaged users actively searching for properties.",
    demographics: {
      age: "25-45 years",
      interests: "Real estate, property investment, home improvement",
      locations: "Urban and suburban areas across multiple regions"
    }
  };

  const adPlacements = [
    {
      id: "banner",
      name: "Header/Footer Banners",
      description: "High-visibility banner ads at the top and bottom of pages.",
      icon: TrendingUp,
    },
    {
      id: "sidebar",
      name: "Sidebar Banners",
      description: "Prominent sidebar ads on listing pages and search results.",
      icon: TrendingUp,
    },
    {
      id: "featured",
      name: "Featured Listings",
      description: "Highlight your properties at the top of search results.",
      icon: CheckCircle,
    },
    {
      id: "email",
      name: "Email Newsletter Sponsorship",
      description: "Reach our engaged subscribers directly in their inbox.",
      icon: Mail,
    },
    {
      id: "in-article",
      name: "In-Article Ads",
      description: "Contextual ads placed within our blog posts and guides.",
      icon: MessageSquare,
    }
  ];

  const benefits = [
    { text: "Targeted Audience: Reach active property seekers and investors.", icon: Users },
    { text: "High Engagement: Our users spend significant time on the platform.", icon: TrendingUp },
    { text: "Increased Visibility: Get your brand and listings in front of thousands.", icon: CheckCircle },
    { text: "Measurable Results: Track your ad performance with detailed analytics.", icon: TrendingUp },
  ];

  const faqs = [
    {
      question: "What kind of businesses can advertise on RentWise?",
      answer: "We welcome advertisements from real estate agencies, property developers, mortgage brokers, home service providers, and other businesses relevant to our audience of renters, buyers, and landlords.",
    },
    {
      question: "How do I track the performance of my ads?",
      answer: "We provide a comprehensive analytics dashboard where you can monitor impressions, clicks, click-through rates (CTR), and other key metrics for your campaigns.",
    },
    {
      question: "What are the accepted ad formats and specifications?",
      answer: "Accepted formats include JPG, PNG, GIF for image ads. Specifications vary by placement and will be provided upon campaign setup. We also support HTML5 ads for certain placements.",
    },
    {
      question: "Can I target specific locations or demographics?",
      answer: "Yes, our platform allows for granular targeting options based on location, property type interest, user demographics, and more to ensure your ads reach the most relevant audience.",
    },
    {
        question: "What is the minimum advertising budget?",
        answer: "Our Basic package starts at $99/month. We also offer custom solutions for larger campaigns. Contact us to discuss your specific needs."
    }
  ];
  
  const testimonials = [
    {
        quote: "Advertising on RentWise significantly boosted our property inquiries. The targeted audience is exactly what we were looking for!",
        name: "Abayomi J.",
        company: "CityScape Realty"
    },
    {
        quote: "The featured listings option gave our premium properties the visibility they deserved. We saw a 200% increase in views.",
        name: "Ebuka B.",
        company: "Luxury Homes Group"
    }
  ];

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
  };

  return (
    <>
      <Head>
        <title>Advertise on RentWise | Reach Your Target Audience</title>
        <meta
          name="description"
          content="Advertise on RentWise and connect with a vast audience of property seekers, renters, and investors. Explore our ad placements and pricing."
        />
      </Head>

      <div className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.section
          className="text-center py-16 bg-gradient-to-r from-[var(--color-tenant-primary)] to-[var(--color-tenant-primary)]/90 text-white rounded-lg shadow-xl"
          initial="hidden"
          animate="visible"
          variants={sectionVariants}
        >
          <motion.h1 
            className="text-5xl font-bold mb-4"
            variants={itemVariants}
          >
            Advertise with RentWise
          </motion.h1>
          <motion.p 
            className="text-xl mb-8 max-w-2xl mx-auto text-white/90"
            variants={itemVariants}
          >
            Connect with a highly engaged audience of property seekers, renters, and real estate professionals.
          </motion.p>
          <motion.div variants={itemVariants}>
            <Button 
              size="lg" 
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-white hover:text-[var(--color-tenant-primary)] transition-colors"
            >
              Get Started Now
            </Button>
          </motion.div>
        </motion.section>

        {/* Traffic Stats Section */}
        <motion.section 
          className="py-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={sectionVariants}
        >
          <h2 className="text-3xl font-semibold text-center mb-12">
            Our Audience & Reach
          </h2>
          <Card className="shadow-lg">
            <CardContent className="p-6 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div variants={itemVariants} className="text-center p-4 rounded-lg bg-[var(--color-tenant-primary)]/5 border border-[var(--color-tenant-primary)]/10">
                <Users className="h-12 w-12 text-[var(--color-tenant-primary)] mx-auto mb-3" />
                <p className="text-2xl font-bold text-[var(--color-neutral-900)]">{trafficStats.monthlyVisitors}</p>
                <p className="text-[var(--color-neutral-600)]">Monthly Visitors</p>
              </motion.div>
              <motion.div variants={itemVariants} className="text-center p-4 rounded-lg bg-[var(--color-cta-success)]/5 border border-[var(--color-cta-success)]/10">
                <TrendingUp className="h-12 w-12 text-[var(--color-cta-success)] mx-auto mb-3" />
                <p className="text-2xl font-bold text-[var(--color-neutral-900)]">{trafficStats.pageViews}</p>
                <p className="text-[var(--color-neutral-600)]">Page Views</p>
              </motion.div>
              <motion.div variants={itemVariants} className="text-center p-4 rounded-lg bg-[var(--color-cta-warning)]/5 border border-[var(--color-cta-warning)]/10">
                <HelpCircle className="h-12 w-12 text-[var(--color-cta-warning)] mx-auto mb-3" /> {/* Placeholder icon */}
                <p className="text-2xl font-bold text-[var(--color-neutral-900)]">{trafficStats.avgSessionDuration}</p>
                <p className="text-[var(--color-neutral-600)]">Avg. Session Duration</p>
              </motion.div>
              <motion.div variants={itemVariants} className="p-4 rounded-lg bg-[var(--color-neutral-50)] border border-[var(--color-neutral-300)]">
                 <h3 className="text-lg font-semibold mb-2 text-center text-[var(--color-neutral-900)]">Key Demographics</h3>
                 <ul className="list-disc list-inside text-sm text-[var(--color-neutral-600)] space-y-1">
                    <li>Age: {trafficStats.demographics.age}</li>
                    <li>Interests: {trafficStats.demographics.interests}</li>
                    <li>Locations: {trafficStats.demographics.locations}</li>
                 </ul>
              </motion.div>
            </CardContent>
            <CardFooter className="p-6 bg-[var(--color-tenant-primary)]/5 rounded-b-lg border-t border-[var(--color-tenant-primary)]/10">
                <p className="text-center w-full text-[var(--color-neutral-700)]">{trafficStats.reach}</p>
            </CardFooter>
          </Card>
        </motion.section>

        {/* Available Ad Placements Section */}
        <motion.section 
          className="py-16 bg-[var(--color-neutral-50)]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={sectionVariants}
        >
          <h2 className="text-3xl font-semibold text-center mb-12 text-[var(--color-neutral-900)]">
            Ad Placement Opportunities
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {adPlacements.map((placement, index) => (
              <motion.div key={placement.id} variants={itemVariants}>
                <Card className="text-center h-full shadow-md hover:shadow-lg transition-shadow border-[var(--color-neutral-300)]">
                  <CardHeader>
                    <placement.icon className={`h-8 w-8 mb-2 mx-auto ${
                      index === 0 ? 'text-[var(--color-tenant-primary)]' :
                      index === 1 ? 'text-[var(--color-cta-success)]' :
                      index === 2 ? 'text-[var(--color-cta-warning)]' :
                      index === 3 ? 'text-[var(--color-cta-accent)]' :
                      'text-[var(--color-featured-primary)]'
                    }`} />
                    <CardTitle className="text-[var(--color-neutral-900)]">{placement.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[var(--color-neutral-600)]">{placement.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Benefits of Advertising Section */}
        <motion.section 
          className="py-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={sectionVariants}
        >
          <h2 className="text-3xl font-semibold text-center mb-12 text-[var(--color-neutral-900)]">
            Why Advertise with RentWise?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={index} 
                className="flex items-start p-4 bg-white rounded-lg shadow border border-[var(--color-neutral-300)]"
                variants={itemVariants}
              >
                <benefit.icon className="h-5 w-5 text-[var(--color-cta-success)] mr-2 shrink-0 mt-0.5" />
                <p className="text-[var(--color-neutral-700)]">{benefit.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>
        
        {/* Contact for Pricing Section */}
        <motion.section
          className="py-16 bg-[var(--color-neutral-50)]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={sectionVariants}
        >
          <div className="text-center">
            <DollarSign className="h-16 w-16 text-[var(--color-cta-success)] mx-auto mb-6" />
            <h2 className="text-3xl font-semibold mb-6 text-[var(--color-neutral-900)]">
              Customized Advertising Solutions
            </h2>
            <p className="text-lg text-[var(--color-neutral-700)] max-w-2xl mx-auto mb-8">
              We offer flexible advertising packages tailored to your specific needs and budget. 
              Our team will work with you to create the most effective campaign.
            </p>
            <p className="text-lg text-[var(--color-neutral-700)] max-w-2xl mx-auto mb-8">
              For detailed pricing information and to discuss your advertising goals, please reach out to our sales team.
            </p>
            <Link href="mailto:sales@rentwise.com?subject=Advertising%20Inquiry">
              <Button size="lg" className="bg-[var(--color-tenant-primary)] text-white hover:bg-[var(--color-tenant-primary)]/90 transition-colors">
                <Mail className="mr-2 h-5 w-5" /> Contact Sales for Pricing
              </Button>
            </Link>
          </div>
        </motion.section>

        {/* Testimonials Section */}
        {testimonials && testimonials.length > 0 && (
            <motion.section
                className="py-16"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={sectionVariants}
            >
                <h2 className="text-3xl font-semibold text-center mb-12 text-[var(--color-neutral-900)]">
                    What Our Advertisers Say
                </h2>
                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {testimonials.map((testimonial, index) => (
                        <motion.div key={index} variants={itemVariants}>
                            <Card className="shadow-lg h-full border border-[var(--color-neutral-300)]">
                                <CardContent className="p-6">
                                    <MessageSquare className="h-8 w-8 text-[var(--color-tenant-primary)] mb-4 opacity-70" />
                                    <p className="text-[var(--color-neutral-700)] italic mb-4">"{testimonial.quote}"</p>
                                    <p className="font-semibold text-[var(--color-neutral-900)]">{testimonial.name}</p>
                                    <p className="text-sm text-[var(--color-neutral-600)]">{testimonial.company}</p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </motion.section>
        )}

        {/* FAQ Section */}
        <motion.section 
          className="py-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={sectionVariants}
        >
          <h2 className="text-3xl font-semibold text-center mb-12 text-[var(--color-neutral-900)]">
            Frequently Asked Questions
          </h2>
          <motion.div variants={itemVariants} className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-[var(--color-neutral-300)]">
                  <AccordionTrigger className="text-lg hover:no-underline text-[var(--color-neutral-900)] hover:text-[var(--color-tenant-primary)] transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[var(--color-neutral-700)]">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </motion.section>

        {/* Call to Action - Final */}
        <motion.section 
          className="py-16 text-center bg-gradient-to-r from-[var(--color-tenant-primary)]/5 to-[var(--color-tenant-primary)]/10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={sectionVariants}
        >
          <h2 className="text-3xl font-semibold mb-6 text-[var(--color-neutral-900)]">Ready to Grow Your Reach?</h2>
          <p className="text-lg text-[var(--color-neutral-700)] max-w-xl mx-auto mb-8">
            Let's get your properties and services in front of the right audience.
          </p>
          <Link href="mailto:sales@rentwise.com?subject=Advertising%20Inquiry">
            <Button size="lg" className="bg-[var(--color-tenant-primary)] text-white hover:bg-[var(--color-tenant-primary)]/90 transition-colors shadow-md hover:shadow-lg">
                <Mail className="mr-2 h-5 w-5" /> Contact Our Team
            </Button>
          </Link>
        </motion.section>
      </div>
    </>
  );
};

export default AdsPage; 